import { useState, useEffect, useCallback } from 'react';

/**
 * Generic hook for API calls with loading and error states
 */
export function useApi(apiCall, dependencies = [], options = {}) {
  const { immediate = true, initialData = null } = options;
  
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  
  const execute = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiCall(...args);
      setData(result.data);
      return result;
    } catch (err) {
      setError(err.message || 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);
  
  useEffect(() => {
    if (immediate) {
      execute().catch(() => {});
    }
  }, dependencies);
  
  return { data, loading, error, execute, setData };
}

/**
 * Hook for paginated API calls
 */
export function usePaginatedApi(apiCall, initialParams = {}) {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useState(initialParams);
  
  const fetchData = useCallback(async (newParams = {}) => {
    setLoading(true);
    setError(null);
    
    const mergedParams = { ...params, ...newParams };
    setParams(mergedParams);
    
    try {
      const result = await apiCall(mergedParams);
      setData(result.data);
      if (result.pagination) {
        setPagination(result.pagination);
      }
      return result;
    } catch (err) {
      setError(err.message || 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiCall, params]);
  
  const goToPage = useCallback((page) => {
    fetchData({ page });
  }, [fetchData]);
  
  const nextPage = useCallback(() => {
    if (pagination.page < pagination.totalPages) {
      goToPage(pagination.page + 1);
    }
  }, [pagination, goToPage]);
  
  const prevPage = useCallback(() => {
    if (pagination.page > 1) {
      goToPage(pagination.page - 1);
    }
  }, [pagination, goToPage]);
  
  useEffect(() => {
    fetchData().catch(() => {});
  }, []);
  
  return {
    data,
    pagination,
    loading,
    error,
    fetchData,
    goToPage,
    nextPage,
    prevPage,
  };
}

/**
 * Debounce hook for search inputs
 */
export function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [value, delay]);
  
  return debouncedValue;
}

/**
 * Local storage hook with type support
 */
export function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return initialValue;
    }
  });
  
  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  };
  
  return [storedValue, setValue];
}
